/**
 * Create-process wizard state + `POST /processes` payload assembly. Kept apart
 * from the presentational step bodies so both stay small and focused.
 */
import type {
  Item,
  ImportItemMetadata,
  ProcessEndpoint,
  ProcessType,
  WarehouseProcess,
} from '@/api/capacities';
import { PROCESS_ENDPOINT_MAP, type EndpointKind } from './process-type-options';

/**
 * Import processes (purchase / return) DEFINE new items to be created on
 * approval — they do NOT pick from existing inventory. Every other type selects
 * existing inventory items.
 */
export const isImportType = (type?: ProcessType): boolean =>
  type === 'import_purchase' || type === 'import_return';

export interface WizardState {
  type?: ProcessType;
  reason: string;
  processDate: string;
  expectedDate: string;
  description: string;
  notes: string;
  source: string;
  destination: string;
  /** Human-readable labels for review display (ids drive the payload). */
  sourceName: string;
  destinationName: string;
  /** Existing inventory items (export / transfer / inventory-check). */
  items: Item[];
  /** New items to create on approval (import_purchase / import_return). */
  importItems: ImportItemMetadata[];
}

/** Map a selected endpoint value onto the id field the API expects for its kind. */
function endpointBody(kind: EndpointKind | undefined, value: string): Record<string, string> | undefined {
  const v = value.trim();
  if (!kind || !v) return undefined;
  if (kind === 'supplier') return { type: 'supplier', supplier_id: v };
  if (kind === 'location') return { type: 'location', location_id: v };
  return { type: 'customer', name: v };
}

/** Shared fields (minus `type`) common to both create and update payloads. */
function commonBody(state: WizardState) {
  const map = state.type ? PROCESS_ENDPOINT_MAP[state.type] : {};
  const base = {
    reason: state.reason.trim() || undefined,
    process_date: state.processDate || undefined,
    expected_date: state.expectedDate || undefined,
    description: state.description.trim() || undefined,
    notes: state.notes.trim() || undefined,
    source: endpointBody(map.source, state.source),
    destination: endpointBody(map.destination, state.destination),
  };
  // Import types carry new-item definitions in `metadata.items`; others reference
  // existing inventory by id.
  if (isImportType(state.type)) {
    return { ...base, metadata: { items: state.importItems } };
  }
  return { ...base, items: state.items.map((it) => ({ item_id: it.id, quantity: 1 })) };
}

/** Assemble the `POST /processes` payload from the wizard state. */
export function buildCreatePayload(state: WizardState) {
  return { type: state.type, ...commonBody(state) };
}

/**
 * Assemble the `PUT /processes/:id` payload. The process `type` is fixed at
 * creation and omitted here (matching legacy `updateProcess`).
 */
export function buildUpdatePayload(state: WizardState) {
  return commonBody(state);
}

/** Reverse `endpointBody`: read the id/name a wizard endpoint field should hold. */
function endpointToWizard(
  endpoint: ProcessEndpoint | undefined,
  kind: EndpointKind | undefined,
): { value: string; name: string } {
  if (!endpoint || !kind) return { value: '', name: '' };
  const name = endpoint.name ?? '';
  if (kind === 'supplier') return { value: endpoint.supplier_id ?? '', name };
  if (kind === 'location') return { value: endpoint.location_id ?? '', name };
  // Customers are free-text: the wizard field holds the name itself.
  return { value: name, name };
}

/**
 * Build wizard state from an existing process for EDIT mode. Existing inventory
 * items come from `process.items` (nested `item`); import definitions come from
 * `process.metadata.items`.
 */
export function processToWizardState(process: WarehouseProcess): WizardState {
  const map = PROCESS_ENDPOINT_MAP[process.type];
  const src = endpointToWizard(process.source, map.source);
  const dst = endpointToWizard(process.destination, map.destination);
  const items = isImportType(process.type)
    ? []
    : (process.items ?? []).map((pi) => pi.item).filter((it): it is Item => Boolean(it));
  const importItems = isImportType(process.type)
    ? ((process.metadata?.items as ImportItemMetadata[] | undefined) ?? [])
    : [];
  return {
    type: process.type,
    reason: process.reason ?? '',
    processDate: process.process_date?.split('T')[0] ?? '',
    expectedDate: process.expected_date?.split('T')[0] ?? '',
    description: process.description ?? '',
    notes: process.notes ?? '',
    source: src.value,
    destination: dst.value,
    sourceName: src.name,
    destinationName: dst.name,
    items,
    importItems,
  };
}

/** Which endpoints a type requires (legacy `getEndpointRequirements`). */
export function endpointRequirements(type?: ProcessType): {
  source: boolean;
  destination: boolean;
} {
  switch (type) {
    case 'import_purchase':
    case 'import_return':
    case 'internal_transfer':
      return { source: true, destination: true };
    case 'inventory_check':
      return { source: true, destination: false };
    default: // export_sale / export_loan / export_delivery
      return { source: false, destination: false };
  }
}

/** Per-field validation errors, keyed by wizard field. Empty = valid. */
export type WizardErrors = Partial<
  Record<'reason' | 'processDate' | 'source' | 'destination' | 'items', string>
>;

/** Validate the required fields for the active type (legacy parity). */
export function validateWizard(state: WizardState): WizardErrors {
  const errors: WizardErrors = {};
  const req = endpointRequirements(state.type);
  const map = state.type ? PROCESS_ENDPOINT_MAP[state.type] : {};
  if (!state.reason.trim()) errors.reason = 'Reason is required';
  if (!state.processDate) errors.processDate = 'Process date is required';
  if (req.source && map.source && !state.source.trim()) errors.source = 'Source is required';
  if (req.destination && map.destination && !state.destination.trim())
    errors.destination = 'Destination is required';
  if (itemCount(state) === 0) errors.items = 'At least one item is required';
  return errors;
}

/** How many items the wizard currently carries, for the active type. */
export const itemCount = (state: WizardState): number =>
  isImportType(state.type) ? state.importItems.length : state.items.length;

/** The wizard is submittable once a type is picked and all required fields pass. */
export const canSubmit = (state: WizardState): boolean =>
  Boolean(state.type) && Object.keys(validateWizard(state)).length === 0;
